/**
 * Admin host for the shared Hint Quality pages.
 *
 * Sits beside the existing Hints list under Operations, and answers a different
 * question: that list is per-session debugging (prompts, model runs, cost);
 * this is per-question review (how much help, how deep, how rated). Same
 * container, deliberately different lens.
 *
 * No feature gate here — the console is already admin-only, and the backend
 * gives an admin caller the unscoped rollup automatically.
 *
 * See docs/FeatureDesigns/HintSystem/hint-quality-review.md
 */

import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  HintQualitySummaryPage, HintQualityQuestionPage,
  type HintQualityView,
} from '@shared-ui/hint-quality';

export function HintQualityTab() {
  const navigate = useNavigate();
  // Local rather than URL state: the Operations page already owns `?tab=`, and
  // a second view param there would collide with its own operation routing.
  const [view, setView] = useState<HintQualityView>('questions');

  return (
    <HintQualitySummaryPage
      view={view}
      onViewChange={setView}
      onQuestionClick={id => navigate(`/hint-quality/${id}`)}
      // A session row opens its own detail, not the question's most recent.
      onSessionClick={row => navigate(`/hint-quality/${row.questionId}?session=${row.id}`)}
    />
  );
}

export function HintQualityQuestionRoute() {
  const navigate = useNavigate();
  const { questionId } = useParams<{ questionId: string }>();
  const [searchParams] = useSearchParams();

  return (
    <HintQualityQuestionPage
      questionId={questionId!}
      initialSessionId={searchParams.get('session') || undefined}
      // Back to the Operations tab this view is hosted in, not browser-history
      // back — the drill-down is often reached from a deep link.
      onBack={() => navigate('/submissions?tab=hintquality')}
    />
  );
}
